<template lang="pug">
  CrmFunnelMemberListUi(
    v-if="ready"
    :members="rows"
    :saving="saving"
    :canManage="canManage"
    @add="addMembers"
    @remove="removeMember"
    )
</template>

<script>
import crmModel from '@/model/crm';
import popupModel from '@/model/popup';
import utils from '@/utils/common';

import crmFunnelApi from '@/api/crmFunnel';
import crmFunnelMemberApi from '@/api/crmFunnelMember';
import companyEmployeeApi from '@/api/companyEmployee';

import CrmFunnelMemberListUi from '@/components/crm/CrmFunnelMemberListUi.vue';
import UserSelectPopupWrappable from '@/components/UserSelectPopupWrappable.vue';

export default {
  name: 'CrmFunnelMemberListApi',
  components: { CrmFunnelMemberListUi },
  props: {
    funnelId: {
      type: [Number, String],
      required: true,
    },
    companyId: {
      type: [Number, String],
      required: true,
    },
    // добавлять и исключать участников могут только владелец компании и
    // заместитель; участнику воронки состав доступен только на просмотр
    canManage: Boolean,
  },
  data() {
    return {
      members: [],
      // сотрудники компании — кандидаты в участники
      employees: [],
      ready: false,
      saving: null,
    };
  },
  created() {
    this.load();
  },
  computed: {
    /**
     * Строки для вёрстки. У только что добавленной строки id ещё не пришёл
     * с сервера: ключом служит сотрудник, а удаление до появления id
     * заблокировано.
     */
    rows() {
      return this.members
        .filter(member => member.employee?.user)
        .map(member => ({
          id: member.id,
          key: member.id ?? member.employee['@id'],
          pending: !member.id,
          user: member.employee.user,
          position: member.employee.position,
        }));
    },
    funnelIri() { return crmFunnelApi.getIri(this.funnelId); },
  },
  methods: {
    async load() {
      const data = await crmModel.run(
        () => Promise.all([
          crmModel.fetchFunnel(this.funnelId),
          companyEmployeeApi.fetchList({ company: this.companyId }, true),
        ]),
        'Не удалось загрузить участников воронки',
      );

      const [funnel, employees] = data ?? [];
      this.members = crmModel.funnelMembers(funnel);
      this.employees = employees?.items ?? [];
      this.ready = true;
    },

    /**
     * Выбор сотрудников: показываем всех сотрудников компании, уже
     * добавленные помечены как «Выбран» и недоступны. Возвращаем сотрудников,
     * а не пользователей — на запись нужен именно employee.
     */
    async selectEmployees() {
      const selected = await popupModel.add({
        wrappable: true,
        component: UserSelectPopupWrappable,
        params: {
          selected: this.rows.map(row => row.user['@id']),
          multiple: true,
          users: this.employees.map(employee => employee.user).filter(Boolean),
          title: 'Добавить участников',
          emptyTitle: 'В компании нет сотрудников',
        },
      });
      if (!selected || !selected.length) return [];

      const byUser = new Map(
        this.employees.filter(e => e.user?.['@id']).map(e => [e.user['@id'], e]),
      );

      return selected.map(user => byUser.get(user['@id'])).filter(Boolean);
    },

    async addMembers() {
      const chosen = await this.selectEmployees();
      if (!chosen.length) return;

      // строки показываем сразу, до ответа: сотрудник уже известен,
      // с сервера нужен только id участника
      const pending = chosen.map(employee => ({ id: null, employee }));
      this.members = [...this.members, ...pending];
      this.saving = true;

      // Пара (воронка, сотрудник) уникальна, поэтому запросы независимы:
      // 422 по одному из них не должен отменять остальные
      const created = await Promise.all(chosen.map(employee => crmFunnelMemberApi
        .create({ funnel: this.funnelIri, employee: employee['@id'] })
        .catch(() => null)));

      // временные строки заменяем настоящими, а не добавившихся убираем
      const next = [];
      this.members.forEach((member) => {
        const index = pending.indexOf(member);
        if (index === -1) {
          next.push(member);
          return;
        }
        if (created[index]) next.push({ ...created[index], employee: member.employee });
      });
      this.members = next;

      const failed = created.filter(member => !member).length;
      // при полной неудаче индикатор убираем совсем: «Сохранено» было бы враньём
      this.saving = failed === created.length ? null : false;

      if (failed) {
        await popupModel.addAlert(
          failed === created.length ? 'Не удалось добавить участников' : 'Не удалось добавить часть участников',
          'Возможно, сотрудник уже добавлен в эту воронку',
        );
      }
    },

    async removeMember(row) {
      // строка ещё сохраняется — id участника с сервера не пришёл, а удалять
      // по нему нечего: фильтр ниже снёс бы заодно все остальные такие строки
      if (!row.id) return;

      const confirmed = await popupModel.addConfirm(
        'Убрать участника из воронки?',
        utils.getUserName(row.user),
      );
      if (!confirmed) return;

      // убираем строку сразу, не дожидаясь ответа; при неудаче возвращаем
      const backup = this.members;
      this.members = this.members.filter(member => member.id !== row.id);
      this.saving = true;

      const result = await crmModel.run(
        () => crmFunnelMemberApi.delete(row.id),
        'Не удалось убрать участника',
      );

      if (result === null) {
        this.members = backup;
        this.saving = null;
        return;
      }

      this.saving = false;
    },
  },
};
</script>
